import { PlusOutlined } from '@ant-design/icons'
import { MutationUpdaterFn, useMutation, useQuery } from '@apollo/client'
import {
  Badge,
  Button,
  Checkbox,
  Col,
  Collapse,
  CollapseProps,
  Divider,
  Form,
  Input,
  List,
  message,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { useForm } from 'antd/lib/form/Form'
import dayjs from 'dayjs'
import React, { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { ACTION_INSERT, ACTION_UPDATE } from '../../apollo/mutation/action.mutation'
import { DEMANDE_UPDATE } from '../../apollo/mutation/demande.mutation'
import { GET_ACTION_QUERY } from '../../apollo/query/action.query'
import { GESTIONNAIRE_QUERY } from '../../apollo/query/personne.query'
import { ETAT_DEMANDE_COLOR } from '../../constants'
import { generateLegacyLink } from '../../helper'
import { useAuth, useQueryCopro, useUpdateKanban } from '../../lib/hooks'
import {
  D4_Action,
  D4_Action_Aggregate,
  D4_Action_Set_Input,
  D4_Demande,
  D4_Demande_Set_Input,
  D4_Fn_View_Demande,
  Personne,
} from '../../types'
import D4Avatar from '../D4Avatar'
import { DDevModalForm } from '../DDevForm'
import { DIModalForm } from '../DIForm'
import InterventionsModal from '../InterventionsModal'
import ModalDemandeEstimation from './ModalDemandeEstimation'
import ModalSelectEvents from './ModalSelectEvents'
import SelectAssurance from './Sinistre/SelectAssurance'
import SelectEquipement from './SelectEquipement'
import { SelectLese, SelectNatureSinistre, SelectOrigineSinistre } from './Sinistre'
import DemandeDetailTags from '../DemandeTable/DemandeDetailTags'

const StyledCollapse: React.FC<{ kanbanColor: string } & CollapseProps> = styled(
  (props: { kanbanColor: string } & CollapseProps) => <Collapse {...props} />
)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background-color: white;
  box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.2);
  .ant-collapse-header {
    border-left: 3px solid ${(props) => props.kanbanColor};
  }
`
const { Paragraph } = Typography
/**
 * Interventions
 */

/**
 * Cloturer une demande:
 * 1. datecloturedefinitive not null
 * 2. datecloturepard4: not null
 * 3. etatdemande: 4
 */

const DemandeHeader = ({ demande }: { demande: D4_Fn_View_Demande }) => {
  const [copro] = useQueryCopro(demande.coproprieteid)
  const [mailModalVisible, setMailModalVisible] = useState(false)
  const [estimationModalVisible, setEstimationModalVisible] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [diModalVisible, setDiModalVisible] = useState(false)
  const [ddevModalVisible, setDdevModalVisible] = useState(false)
  const { user } = useAuth()
  const { updateKanban, loading, data } = useUpdateKanban(demande.id)

  const { data: actions } = useQuery(GET_ACTION_QUERY, {
    variables: {
      demandeId: demande.id,
    },
  })

  const showModal = () => {
    setIsModalVisible(true)
  }

  const [updateDemande] = useMutation<{ demande: D4_Demande }, { id: number; data: D4_Demande_Set_Input }>(
    DEMANDE_UPDATE
  )

  const handleTitleChange = (v: string) =>
    updateDemande({
      variables: {
        id: demande.id,
        data: { description: v },
      },
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ __typename: 'd4_fn_view_demande', id: data?.demande.id }),
          fields: {
            description() {
              return data?.demande.description
            },
          },
        })
      },
    })

  const handleCloseDemande = (toggle: any) => {
    updateDemande({
      variables: {
        id: demande.id,
        data: {
          datecloturedefinitive: toggle ? dayjs() : null,
          datecloturepard4: toggle ? dayjs() : null,
          etatdemande: toggle ? 4 : 1,
        },
      },
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ id: data?.demande.id, __typename: 'd4_fn_view_demande' }),
          fields: {
            datecloturedefinitive() {
              return data?.demande.datecloturedefinitive
            },
            datecloturepard4() {
              return data?.demande.datecloturepard4
            },
            etatdemande() {
              return data?.demande.etatdemande
            },
          },
        })
      },
    })
  }

  const handleUrgence = async (v: any) => {
    await updateDemande({
      variables: { id: demande.id, data: { urgence: v.target.checked ? 2 : null } },
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ id: data?.demande.id, __typename: 'd4_fn_view_demande' }),
          fields: {
            urgence() {
              return data?.demande.urgence
            },
          },
        })
      },
    })
  }

  const suiviCs = async (v: any) => {
    await updateDemande({
      variables: { id: demande.id, data: { suivics: v.target.checked } },
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ id: data?.demande.id, __typename: 'd4_fn_view_demande' }),
          fields: {
            suivics() {
              return data?.demande.suivics
            },
          },
        })
      },
    })
  }
  const dossierPermanent = async (v: any) => {
    await updateDemande({
      variables: { id: demande.id, data: { dossierpermanent: v.target.checked } },
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ id: data?.demande.id, __typename: 'd4_fn_view_demande' }),
          fields: {
            dossierpermanent() {
              return data?.demande.dossierpermanent
            },
          },
        })
      },
    })
  }

  const kanbanOptions = useMemo(
    () =>
      data?.map((k) => ({
        label: k.titre,
        value: k.id,
        etatkanbanid: k.etatkanbans[0].id,
      })),
    [data]
  )

  const etatKanbanOptions = useMemo(
    () =>
      data
        ?.find((k) => Number(k.id) === Number(demande.kanbanid))
        ?.etatkanbans.map((ek) => ({
          label: ek.libelle,
          value: ek.id,
          etatkanbanid: ek.id,
          color: ek.couleuretatkanban,
        })) ?? [],
    [demande.kanbanid, data]
  )

  const genererdDeclaration = async (idDemande: number) => {
    const res = await fetch(
      `https://vision.d4immobilier.fr/GenererDeclarationSinistre?id=${idDemande}&jwt=${localStorage.getItem('jwt')}`
    )
    if (res.status === 200) {
      message.success('La déclaration a été générée (normalement !)')
    } else {
      message.error('Une erreur est survenue')
    }
  }
  if (!demande.id) return null

  return (
    <>
      <StyledCollapse
        ghost
        kanbanColor={ETAT_DEMANDE_COLOR[demande.couleuretatkanban] ?? -1}
        defaultActiveKey="description"
      >
        <Collapse.Panel
          header={
            <Row style={{ width: '100%' }}>
              <Col>
                <Paragraph
                  editable={{ onChange: handleTitleChange }}
                  strong
                  style={{ display: 'inline', paddingRight: '40px' }}
                >
                  {demande.description?.trim()}
                </Paragraph>
              </Col>
              <Col>
                <DemandeDetailTags item={demande} />
                {demande.numero && <Typography.Text type="secondary">({demande.numero})</Typography.Text>}
              </Col>
            </Row>
          }
          key="description"
        >
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <Row gutter={[12, 12]}>
                <Col xs={24} md={8}>
                  <Select
                    placeholder="Kanban"
                    style={{ width: '100%' }}
                    getPopupContainer={(node) => node}
                    value={demande.kanbanid}
                    options={kanbanOptions}
                    //@ts-expect-error
                    onSelect={updateKanban}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Select
                    placeholder="État Kanban"
                    style={{ width: '100%' }}
                    getPopupContainer={(node) => node}
                    value={demande.etatkanbanid}
                    //@ts-expect-error
                    onSelect={updateKanban}
                    loading={loading}
                  >
                    {etatKanbanOptions.map((ek) => (
                      <Select.Option
                        key={ek.value}
                        style={{ borderLeft: `3px solid ${ETAT_DEMANDE_COLOR[ek.color] ?? -1}` }}
                        etatkanbanid={ek.value}
                        value={ek.value}
                      >
                        {ek.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col>
                  <Row>
                    <SelectEquipement demande={demande} />
                    <InterventionsModal
                      equipementId={demande.equipementid}
                      typeequipementId={demande.typeequipementid}
                      trigramme={copro?.trigramme ?? ''}
                    />
                  </Row>
                  <Typography.Text type="secondary">
                    fournisseurs pour ce type d'equipement :{' '}
                    {demande.equipement?.fournisseur_favori_equipements
                      .map((four) => four.societe.raisonsociale)
                      .join(', ')}{' '}
                  </Typography.Text>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              {(demande.kanbantitre === 'Sinistres' || demande.dtype === 'Sinistre') && (
                <Row gutter={[12, 12]}>
                  <Col span={24}>
                    <SelectAssurance
                      demandeId={demande.id}
                      coproprieteId={demande.coproprieteid}
                      assuranceId={demande.assuranceid}
                    />
                  </Col>
                  <Col>
                    <SelectNatureSinistre demandeId={demande.id} />
                  </Col>
                  <Col>
                    <SelectLese demande={demande} />
                  </Col>
                  <Col>
                    <SelectOrigineSinistre demande={demande} />
                  </Col>
                </Row>
              )}
            </Col>
            <Col span={24}>
              <Row justify="space-between">
                <Col>
                  {demande.dtype === 'Sinistre' && (
                    <Button type="link" onClick={() => genererdDeclaration(demande.id)}>
                      Générer déclaration
                    </Button>
                  )}
                  <Button type="link" onClick={() => setDiModalVisible(true)}>
                    {/* <Link
                      to={generateLegacyLink({
                        url: `/copropriete/${copro?.trigramme}`,
                        path: 'demande_intervention.jsp',
                        id: demande.id,
                      })}
                      target="_blank"
                      rel="noreferrer"
                    > */}
                    DI
                    {/* </Link> */}
                  </Button>
                  <Button type="link" onClick={() => setDdevModalVisible(true)}>
                    DDev
                  </Button>
                  <Button type="link" onClick={() => setMailModalVisible(true)}>
                    Mail
                  </Button>
                  <Button type="link">
                    <Link
                      to={generateLegacyLink({
                        url: `/copropriete/${copro?.trigramme}`,
                        path: 'rediger_courrier.jsp',
                        id: demande.id,
                      })}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Courrier
                    </Link>
                  </Button>
                  <Badge count={actions?.actions?.filter((action: D4_Action) => !action.daterealisation).length}>
                    <Button type="link" onClick={showModal}>
                      Tâches
                    </Button>
                  </Badge>
                  <Button type="link">
                    <Link
                      to={generateLegacyLink({
                        url: `/copropriete/${copro?.trigramme}`,
                        path: 'commande_moyen_acces.jsp?idDemande=' + demande.id,
                        id: -1,
                      })}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Cde Bip
                    </Link>
                  </Button>
                  <Button type="link" onClick={() => setEstimationModalVisible(true)}>
                    Demande d'estimation
                  </Button>
                </Col>
                <Col>
                  <Space>
                    <Checkbox checked={demande?.suivics ? true : false} onChange={suiviCs}>
                      Suivi CS
                    </Checkbox>
                    <Checkbox checked={demande?.dossierpermanent ? true : false} onChange={dossierPermanent}>
                      Dossier permanent
                    </Checkbox>
                    <Checkbox checked={demande.urgence === 2} onChange={handleUrgence}>
                      Demande urgente
                    </Checkbox>
                    <Switch
                      onChange={handleCloseDemande}
                      title="Clôturer"
                      checkedChildren="Rouvrir"
                      unCheckedChildren="Clôturer"
                      defaultChecked={demande.etatdemande !== 1}
                    />
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>
        </Collapse.Panel>
      </StyledCollapse>
      <ModalSelectEvents
        evenements={demande.evenements}
        visible={mailModalVisible}
        onOk={() => setMailModalVisible(false)}
        onCancel={() => setMailModalVisible(false)}
        demande={demande}
      />
      <ModalDemandeEstimation
        evenements={demande.evenements}
        visible={estimationModalVisible}
        onOk={() => setEstimationModalVisible(false)}
        onCancel={() => setEstimationModalVisible(false)}
        demande={demande}
      />
      <DIModalForm
        demande={demande}
        visible={diModalVisible}
        footer={[]}
        onCancel={() => setDiModalVisible(false)}
        width="100%"
      />
      <DDevModalForm
        demande={demande}
        visible={ddevModalVisible}
        footer={[]}
        onCancel={() => setDdevModalVisible(false)}
        width="100%"
      />
      <Modal
        title="Tâches"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[<Button onClick={() => setIsModalVisible(false)}>Fermer</Button>]}
      >
        <CreateActionForm
          emetteur_id={Number(user.personne.id)}
          demande_id={demande.id}
          copropriete_id={demande.coproprieteid}
        />
      </Modal>
    </>
  )
}

const updateDemande: MutationUpdaterFn<{ action: D4_Action }> = (cache, { data }) => {
  cache.modify({
    id: cache.identify({ __typename: 'd4_fn_view_demande', id: data?.action.demande_id }),
    fields: {
      actions_aggregate(current: D4_Action_Aggregate) {
        const currentCount = current?.aggregate?.count ?? 0
        const count = data?.action.daterealisation && currentCount >= 0 ? currentCount - 1 : currentCount + 1
        return { aggregate: { count } }
      },
    },
  })
}

const CreateActionForm = ({
  emetteur_id,
  demande_id,
  copropriete_id,
}: {
  emetteur_id: number
  demande_id: number
  copropriete_id: number
}) => {
  const { data, loading } = useQuery<{ gestionnaires: Personne[] }>(GESTIONNAIRE_QUERY)
  const [form] = useForm()
  const [insertAction] = useMutation(ACTION_INSERT, {
    refetchQueries: [{ query: GET_ACTION_QUERY, variables: { demandeId: demande_id } }],
    update: updateDemande,
  })

  const [updateAction] = useMutation<{ action: D4_Action }, { id: number; data: D4_Action_Set_Input }>(ACTION_UPDATE, {
    update: updateDemande,
  })

  const { data: actions } = useQuery<{ actions: D4_Action[] }>(GET_ACTION_QUERY, {
    variables: {
      demandeId: demande_id,
    },
  })

  const handleOnFinish = useCallback(
    async (values: { executeur_id: number; libelle: string }) => {
      const data = {
        ...values,
        emetteur_id,
        demande_id,
        copropriete_id,
        dtype: 'ActionDemande',
      }

      await insertAction({ variables: { data } })
      form.resetFields()
    },
    [form, copropriete_id, demande_id, emetteur_id, insertAction]
  )

  const handleCheck = (e: CheckboxChangeEvent, actionId: number) => {
    e.preventDefault()
    if (e.target.checked) {
      updateAction({ variables: { id: actionId, data: { daterealisation: dayjs() } } })
    } else {
      updateAction({ variables: { id: actionId, data: { daterealisation: null } } })
    }
  }

  if (loading || !actions) return null

  const { gestionnaires } = data ?? {}

  return (
    <>
      <Form form={form} name="action-form" onFinish={handleOnFinish}>
        <Form.Item name="executeur_id" rules={[{ required: true, message: 'Il faut selectionner un gestionnaire' }]}>
          <Select
            placeholder="Selectionnez un gestionnaire"
            getPopupContainer={(container) => container}
            style={{ width: '100%' }}
            options={gestionnaires?.map((gestionnaire) => ({
              label: `${gestionnaire.prenom} ${gestionnaire.nom}`,
              value: gestionnaire.id,
            }))}
            filterOption
            optionFilterProp="label"
            showSearch
          />
        </Form.Item>
        <Form.Item name="libelle" rules={[{ required: true, message: 'Le libelle est obligatoire' }]}>
          <Input placeholder="Libelle" />
        </Form.Item>
        <Button htmlType="submit" type="primary" icon={<PlusOutlined />}>
          Ajouter
        </Button>
      </Form>
      <Divider>Tâches existantes</Divider>
      <List
        locale={{ emptyText: 'Pas de tâches' }}
        dataSource={actions.actions}
        renderItem={(tache) => (
          <List.Item
            actions={[<Checkbox checked={tache.daterealisation} onChange={(e) => handleCheck(e, tache.id)} />]}
          >
            <List.Item.Meta
              avatar={<D4Avatar nom={tache.emetteur?.nom} prenom={tache.emetteur?.prenom} role="gestionnaire" />}
              title={<a href="https://ant.design">{tache.libelle}</a>}
              description={
                <div>
                  Assignée à {tache.executeur?.prenom} {tache.executeur?.nom} <br /> le{' '}
                  {dayjs(tache.datecreation).format('DD/MM/YY')} <br />
                  {tache.daterealisation && `effectuée le ${dayjs(tache.daterealisation).format('DD/MM/YY')}`}
                </div>
              }
            />
            {/* <Checkbox /> */}
          </List.Item>
        )}
      />
    </>
  )
}

export default DemandeHeader
